import { useState, useEffect, useCallback, useRef, useMemo, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHashtag, faClock, faSpinner, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import KpiElement, { MetricHoverBorder } from './kpiElement.js';
import ChatHistoryLineChart from './chatHistoryChart.js';
import CountryDistributionMap from './countryDistributionMap.js';
import './analytics.css';
import { useTranslation } from 'react-i18next';
import '../translations.js';

const Insights = ({ bots = [], selectedBotIds = [], startDate, endDate, addBot }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const isFirstLoadRef = useRef(true);
  const [chatHistory, setChatHistory] = useState([]);
  const [msgKpiHovered, setMsgKpiHovered] = useState(false);
  const [msgKpiTooltipPos, setMsgKpiTooltipPos] = useState({ top: 0, left: 0 });
  const msgKpiRef = useRef(null);
  const [uniqueSessionCount, setUniqueSessionCount] = useState(0);
  const [avgMessagesPerSession, setAvgMessagesPerSession] = useState(0);
  const [totalMessages, setTotalMessages] = useState(0);
  const [avgBotResponseTime, setAvgBotResponseTime] = useState(0);

  const messageUsage = useMemo(() => {
    const selected = bots.filter(b => selectedBotIds.includes(b.id));
    if (selected.length === 0) return null;
    const totalUsed = selected.reduce((sum, b) => sum + (b.current_message_count ?? 0), 0);
    const totalLimit = selected.reduce((sum, b) => sum + (b.subscription_plan?.message_limit ?? 0), 0);
    if (totalLimit === 0) return null;
    return { totalUsed, totalLimit, bots: selected };
  }, [bots, selectedBotIds]);

  const perBotStats = useMemo(() => {
    if (!chatHistory.length) return [];
    const statsMap = {};
    chatHistory.forEach(chat => {
      const id = chat.bot_id;
      if (!statsMap[id]) {
        statsMap[id] = { id, name: chat.bot_name || id, sessions: new Set(), messages: 0, responseTimes: [], entries: [] };
      }
      statsMap[id].sessions.add(chat.session_id);
      if (chat.type === 'user_input') statsMap[id].messages++;
      statsMap[id].entries.push(chat);
    });
    return Object.values(statsMap).map(s => {
      const sessionCount = s.sessions.size;
      const avg = sessionCount > 0 ? s.messages / sessionCount : 0;
      // response times per bot
      const groups = {};
      s.entries.forEach(e => {
        if (!groups[e.session_id]) groups[e.session_id] = [];
        groups[e.session_id].push(e);
      });
      const rts = [];
      Object.values(groups).forEach(arr => {
        arr.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        for (let i = 0; i < arr.length; i++) {
          if (arr[i].type === 'user_input') {
            for (let j = i + 1; j < arr.length; j++) {
              if (arr[j].type === 'assistant') {
                const delta = (new Date(arr[j].timestamp) - new Date(arr[i].timestamp)) / 1000;
                if (delta >= 0 && delta <= 600) rts.push(delta);
                break;
              }
            }
          }
        }
      });
      let avgRt = 0;
      if (rts.length > 0) {
        const raw = rts.reduce((a, b) => a + b, 0) / rts.length;
        avgRt = raw > 4 ? raw - 2 : raw;
      }
      return { name: s.name, sessions: sessionCount, messages: s.messages, avgMsgs: avg, avgResponseTime: avgRt };
    });
  }, [chatHistory]);

  useLayoutEffect(() => {
    if (msgKpiHovered && msgKpiRef.current) {
      const rect = msgKpiRef.current.getBoundingClientRect();
      const tooltipWidth = 300;
      const padding = 12;
      const viewportWidth = window.innerWidth;

      let left = rect.left + rect.width / 2;
      if (left - tooltipWidth / 2 < padding) {
        left = padding + tooltipWidth / 2;
      }
      if (left + tooltipWidth / 2 > viewportWidth - padding) {
        left = viewportWidth - padding - tooltipWidth / 2;
      }

      setMsgKpiTooltipPos({ top: rect.bottom + 10, left });
    }
  }, [msgKpiHovered]);

  const fetchChatHistory = useCallback(async () => {
    if (isFirstLoadRef.current) setLoading(true);
    try {
      if (!selectedBotIds.length) {
        setChatHistory([]);
        setUniqueSessionCount(0);
        setAvgMessagesPerSession(0);
        setTotalMessages(0);
        setAvgBotResponseTime(0);
        return;
      }

      const promises = selectedBotIds.map(botId => {
        const url = `${process.env.REACT_APP_API_BASE_URL}chat_history?bot_id=${botId}&start_date=${startDate}&end_date=${endDate}`;
        return fetch(url).then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.json();
        });
      });

      const results = await Promise.all(promises);
      const combinedData = results.flat();

      const enhancedData = combinedData.map(chat => {
        const bot = (bots || []).find(b => b.id === chat.bot_id);
        return { ...chat, bot_name: bot ? bot.name : '' };
      });

      setChatHistory(enhancedData.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));

      const sessionIds = new Set(enhancedData.map((chat) => chat.session_id));
      const uniqueCount = sessionIds.size;

      let totalMessagesCount = 0;
      const messagesPerSession = {};

      enhancedData.forEach((chat) => {
        const { session_id, type, timestamp } = chat;

        if (type === 'user_input') {
          totalMessagesCount++;
        }

        if (!messagesPerSession[session_id]) {
          messagesPerSession[session_id] = { user_input_count: 0, timestamps: [], date: new Date(timestamp).toISOString() };
        }
        if (type === 'user_input') {
          messagesPerSession[session_id].user_input_count++;
        }
        messagesPerSession[session_id].timestamps.push(new Date(timestamp));
      });

      // Calculate average bot response time (time between user_input and assistant response)
      const responseTimes = [];
      const sessionGroups = {};
      enhancedData.forEach((chat) => {
        if (!sessionGroups[chat.session_id]) {
          sessionGroups[chat.session_id] = [];
        }
        sessionGroups[chat.session_id].push(chat);
      });

      Object.values(sessionGroups).forEach((sessionElements) => {
        sessionElements.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        for (let i = 0; i < sessionElements.length; i++) {
          const elem = sessionElements[i];
          if (elem.type === 'user_input') {
            for (let j = i + 1; j < sessionElements.length; j++) {
              if (sessionElements[j].type === 'assistant') {
                const delta = (new Date(sessionElements[j].timestamp) - new Date(elem.timestamp)) / 1000;
                if (delta >= 0 && delta <= 600) {
                  responseTimes.push(delta);
                }
                break;
              }
            }
          }
        }
      });

      let avgResponseTime = 0;
      if (responseTimes.length > 0) {
        const avgRaw = responseTimes.reduce((sum, t) => sum + t, 0) / responseTimes.length;
        avgResponseTime = avgRaw > 4 ? avgRaw - 2 : avgRaw;
      }

      setUniqueSessionCount(uniqueCount);
      setAvgMessagesPerSession(totalMessagesCount / uniqueCount || 0);
      setTotalMessages(totalMessagesCount);
      setAvgBotResponseTime(avgResponseTime);
    } catch (err) {
      console.error('Error fetching chat history:', err);
    } finally {
      setLoading(false);
      isFirstLoadRef.current = false;
      setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
      }, 100);
    }
  }, [selectedBotIds, startDate, endDate, bots]);

  useEffect(() => {
    fetchChatHistory();
  }, [fetchChatHistory, selectedBotIds, startDate, endDate]);

  if (loading) {
    return (
      <div className="loading-container">
        <FontAwesomeIcon icon={faSpinner} spin className="main-loading-spinner" />
      </div>
    );
  }

  if ((bots?.length ?? 0) === 0) {
    return (
      <div className="bots-overview">
        <div className="bots-overview-header">
          <h1>⚠️ {t('no_bots_yet')}</h1>
        </div>
        <button className="create-here-button" onClick={addBot}>
          {t('create_here')}
        </button>
      </div>
    );
  }

  return (
    <div className="analytics-dashboard">
      <div>
        <div className="kpi-container">
          <KpiElement metricValue={uniqueSessionCount} metricTitle={t('count_chats')} icon={faHashtag}>
            {perBotStats.length > 0 && (
              <>
                <div className="kpi-usage-tooltip-title">{t('count_chats')}</div>
                <div className="kpi-usage-tooltip-desc">{t('kpi_desc_count_chats')}</div>
                {perBotStats.map(b => (
                  <div key={b.name} className="kpi-usage-tooltip-row">
                    <span className="kpi-usage-tooltip-name">{b.name}</span>
                    <span className="kpi-usage-tooltip-values">{b.sessions.toLocaleString('de-CH')}</span>
                  </div>
                ))}
              </>
            )}
          </KpiElement>
          <KpiElement metricValue={avgMessagesPerSession.toFixed(2)} metricTitle={t('avg_msgs_per_chat')} icon={faHashtag}>
            {perBotStats.length > 0 && (
              <>
                <div className="kpi-usage-tooltip-title">{t('avg_msgs_per_chat')}</div>
                <div className="kpi-usage-tooltip-desc">{t('kpi_desc_avg_msgs')}</div>
                {perBotStats.map(b => (
                  <div key={b.name} className="kpi-usage-tooltip-row">
                    <span className="kpi-usage-tooltip-name">{b.name}</span>
                    <span className="kpi-usage-tooltip-values">{b.avgMsgs.toFixed(2)}</span>
                  </div>
                ))}
              </>
            )}
          </KpiElement>
          <KpiElement metricValue={totalMessages} metricTitle={t('total_messages')} icon={faHashtag}>
            {perBotStats.length > 0 && (
              <>
                <div className="kpi-usage-tooltip-title">{t('total_messages')}</div>
                <div className="kpi-usage-tooltip-desc">{t('kpi_desc_total_msgs')}</div>
                {perBotStats.map(b => (
                  <div key={b.name} className="kpi-usage-tooltip-row">
                    <span className="kpi-usage-tooltip-name">{b.name}</span>
                    <span className="kpi-usage-tooltip-values">{b.messages.toLocaleString('de-CH')}</span>
                  </div>
                ))}
              </>
            )}
          </KpiElement>
          <KpiElement metricValue={avgBotResponseTime.toFixed(2)} metricTitle={t('avg_response_time')} icon={faClock}>
            {perBotStats.length > 0 && (
              <>
                <div className="kpi-usage-tooltip-title">{t('avg_response_time')}</div>
                <div className="kpi-usage-tooltip-desc">{t('kpi_desc_response_time')}</div>
                {perBotStats.map(b => (
                  <div key={b.name} className="kpi-usage-tooltip-row">
                    <span className="kpi-usage-tooltip-name">{b.name}</span>
                    <span className="kpi-usage-tooltip-values">{b.avgResponseTime.toFixed(2)}s</span>
                  </div>
                ))}
              </>
            )}
          </KpiElement>
          {messageUsage && (
            <div
              className="metric kpi-usage-metric"
              ref={msgKpiRef}
              onMouseEnter={() => setMsgKpiHovered(true)}
              onMouseLeave={() => setMsgKpiHovered(false)}
            >
              <MetricHoverBorder />
              <div className="metric-content">
                <p className="metric-header">
                  <FontAwesomeIcon icon={faEnvelope} className="metric-icon" />
                  {t('messages_used')}
                </p>
                <p className="metric-value">
                  {messageUsage.totalUsed.toLocaleString('de-CH')}
                  <span className="kpi-usage-limit"> / {messageUsage.totalLimit.toLocaleString('de-CH')}</span>
                </p>
              </div>
              {msgKpiHovered && createPortal(
                <div
                  className="kpi-usage-tooltip"
                  style={{ top: `${msgKpiTooltipPos.top}px`, left: `${msgKpiTooltipPos.left}px` }}
                >
                  <div className="kpi-usage-tooltip-title">{t('messages_used')}</div>
                  <div className="kpi-usage-tooltip-desc">{t('kpi_desc_messages_used')}</div>
                  {messageUsage.bots.map(b => {
                    const used = b.current_message_count ?? 0;
                    const limit = b.subscription_plan?.message_limit ?? 0;
                    const pct = limit > 0 ? Math.min(Math.round((used / limit) * 100), 100) : 0;
                    return (
                      <div key={b.id} className="kpi-usage-tooltip-row">
                        <span className="kpi-usage-tooltip-name">{b.name}</span>
                        <div className="kpi-usage-tooltip-bar-track">
                          <div
                            className={`kpi-usage-tooltip-bar-fill${pct >= 100 ? ' kpi-usage-tooltip-bar-full' : ''}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="kpi-usage-tooltip-values">{used.toLocaleString('de-CH')} / {limit.toLocaleString('de-CH')}</span>
                      </div>
                    );
                  })}
                </div>,
                document.body
              )}
            </div>
          )}
        </div>

        <CountryDistributionMap
          selectedBotIds={selectedBotIds}
          startDate={startDate}
          endDate={endDate}
        />

        <div className="charts-parent-container">
          <ChatHistoryLineChart
            chatData={chatHistory}
            startDate={startDate}
            endDate={endDate}
            title={'count_chats'}
          />
          <ChatHistoryLineChart
            chatData={chatHistory}
            startDate={startDate}
            endDate={endDate}
            dataType={'total_user_input'}
            title={'count_messages'}
          />
        </div>
      </div>
    </div>
  );
};

export default Insights;
